import React, { useEffect, useState } from 'react';
import { employeeService } from '../services/api';
import './EmployeeDefaultBonusEditor.css';

const SUGGESTED_BONUS_NAMES = [
  'prime de présence',
  'prime de panier',
  'prime de douche',
  'prime de rendement mensuel',
  'prime de transport',
  'prime de lait',
  'prime de production',
  'prime de fonction',
  'prime de responsabilité',
  'prime de déplacement',
];

const emptyRow = () => ({ bonusName: '', amount: '' });

const normalizeBonusKey = (name) =>
  (name || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

/** Affichage et saisie avec 3 décimales (ex. 7,880) */
const formatAmount3 = (value) => {
  if (value == null || value === '') return '';
  const n = parseFloat(String(value).replace(',', '.'));
  if (Number.isNaN(n)) return '';
  return n.toFixed(3);
};

const parseAmount3 = (value) => {
  const n = parseFloat(String(value).replace(',', '.'));
  if (Number.isNaN(n) || n < 0) return 0;
  return Math.round(n * 1000) / 1000;
};

const EmployeeDefaultBonusEditor = ({ employee, onClose, onSaved }) => {
  const [rows, setRows] = useState([emptyRow()]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!employee?.id) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await employeeService.getDefaultBonuses(employee.id);
        const data = response.data || [];
        if (data.length > 0) {
          setRows(
            data.map((b) => ({
              id: b.id,
              bonusName: b.bonusName || '',
              amount: b.amount != null ? formatAmount3(b.amount) : '',
            }))
          );
        } else {
          setRows([emptyRow()]);
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Erreur lors du chargement des primes');
        setRows([emptyRow()]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [employee?.id]);

  const updateRow = (index, field, value) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const addRow = () => {
    setRows((prev) => [...prev, emptyRow()]);
  };

  const removeRow = (index) => {
    setRows((prev) => (prev.length <= 1 ? [emptyRow()] : prev.filter((_, i) => i !== index)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const seen = new Set();
      const payload = [];
      for (const r of rows) {
        const bonusName = (r.bonusName || '').trim();
        const amount = parseAmount3(r.amount);
        if (!bonusName || amount <= 0) continue;
        const key = normalizeBonusKey(bonusName);
        if (seen.has(key)) {
          setError(`Nom de prime en double : ${bonusName}`);
          setSaving(false);
          return;
        }
        seen.add(key);
        payload.push({ bonusName, amount });
      }

      const response = await employeeService.saveDefaultBonuses(employee.id, payload);
      if (onSaved) onSaved(response.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const employeeLabel = employee
    ? `${employee.nom || ''} ${employee.prenom || ''}`.trim()
    : '';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content default-bonus-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Primes par défaut — {employeeLabel}</h2>
          <button type="button" className="close-button" onClick={onClose} aria-label="Fermer">
            ×
          </button>
        </div>

        <form className="modal-body default-bonus-body" onSubmit={handleSubmit}>
          <p className="default-bonus-hint">
            Montants <strong>mensuels</strong> (proratisés sur 26 jours à la création de la paie).
            La prime de rendement mensuel peut être recalculée automatiquement selon le salaire de base.
          </p>

          {error && <div className="default-bonus-error">{error}</div>}

          {loading ? (
            <div className="loading">Chargement...</div>
          ) : (
            <>
              <datalist id="suggested-bonus-names">
                {SUGGESTED_BONUS_NAMES.map((name) => (
                  <option key={name} value={name} />
                ))}
              </datalist>

              <table className="default-bonus-table">
                <thead>
                  <tr>
                    <th>Nom de la prime</th>
                    <th>Montant mensuel (DT)</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={row.id ?? `row-${index}`}>
                      <td>
                        <input
                          type="text"
                          list="suggested-bonus-names"
                          value={row.bonusName}
                          onChange={(e) => updateRow(index, 'bonusName', e.target.value)}
                          placeholder="Ex. prime de présence"
                          className="default-bonus-input"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.001"
                          value={row.amount}
                          onChange={(e) => updateRow(index, 'amount', e.target.value)}
                          onBlur={(e) => updateRow(index, 'amount', formatAmount3(e.target.value))}
                          placeholder="0,000"
                          className="default-bonus-input amount-input"
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => removeRow(index)}
                          title="Supprimer la ligne"
                        >
                          −
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <button type="button" className="btn btn-secondary add-bonus-row-btn" onClick={addRow}>
                + Ajouter une prime
              </button>
            </>
          )}

          <div className="default-bonus-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
              Annuler
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading || saving}>
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmployeeDefaultBonusEditor;
